import { cn, initials } from '@/lib/utils';
import { useAuth } from '@/app/providers';
import type { Profile } from '@/types';
import { ProfilePreviewCard } from '@/features/profile/components/ProfilePreviewCard';
import { FriendshipInfoRail } from '@/features/dm/components/FriendshipInfoRail';
import { getEmblem, getTitle } from '@/features/profile/cosmetics';
import { stripHandle } from '@/features/profile/helpers';
import { useProfileTheme, ProfileThemePreview } from '@/features/profile/hooks/useProfileTheme';
import { ProfileBannerSurface } from '@/components/theme/ProfileBannerSurface';
import { gradientTextStyle } from '@/components/theme/CommunityThemeScope';
import { avatarFrameStyle } from '../model/profileDoc';
import { EmblemGlyph } from '@/features/profile/components/EmblemGlyph';
import {
  useProfileEditor,
  type PreviewContextId,
  type PreviewWidth,
} from '../state/profileEditorStore';

/**
 * Inspect-tool-style context switcher for Profile Studio. The previews are the
 * REAL production components (ProfilePreviewCard, FriendshipInfoRail) rendered
 * at their real widths, fed the unpublished draft through the same
 * useProfileTheme hook the live app uses — what you see here is what ships.
 * The chat-popup header mirrors UserProfileModal's exact header markup
 * (430px / 178px banner / 98px avatar).
 */

const CONTEXTS: { id: PreviewContextId; label: string }[] = [
  { id: 'hero', label: 'Full profile' },
  { id: 'popup', label: 'Chat popup' },
  { id: 'dm', label: 'DM view' },
];

const WIDTHS: { id: PreviewWidth; label: string; px: number }[] = [
  { id: 'desktop', label: 'Desktop', px: 760 },
  { id: 'tablet', label: 'Tablet', px: 560 },
  { id: 'mobile', label: 'Mobile', px: 360 },
];

export function PreviewSwitcher() {
  const { profile } = useAuth();
  const doc = useProfileEditor((s) => s.doc);
  const context = useProfileEditor((s) => s.previewContext);
  const width = useProfileEditor((s) => s.previewWidth);
  const setPreview = useProfileEditor((s) => s.setPreview);

  if (!profile) {
    return (
      <div className="grid flex-1 place-items-center text-[13px] font-semibold text-white/40">
        Sign in to design your profile.
      </div>
    );
  }

  const widthPx = WIDTHS.find((w) => w.id === width)!.px;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex flex-none flex-wrap items-center justify-between gap-2 border-b border-white/[0.06] bg-ink-900 px-4 py-2">
        <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-black/20 p-0.5">
          {CONTEXTS.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setPreview(c.id)}
              className={cn(
                'rounded-md px-2.5 py-1.5 text-[12px] font-bold transition-colors',
                context === c.id ? 'bg-white/[0.14] text-white' : 'text-white/45 hover:text-white/80',
              )}
            >
              {c.label}
            </button>
          ))}
        </div>
        {context === 'hero' && (
          <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-black/20 p-0.5">
            {WIDTHS.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => setPreview(undefined, w.id)}
                className={cn(
                  'rounded-md px-2.5 py-1.5 text-[11px] font-bold transition-colors',
                  width === w.id ? 'bg-white/[0.14] text-white' : 'text-white/45 hover:text-white/80',
                )}
              >
                {w.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 items-start justify-center overflow-auto bg-ink-950 p-8">
        <ProfileThemePreview doc={doc}>
          {context === 'hero' && (
            <div style={{ width: widthPx }} className="max-w-full">
              <ProfilePreviewCard
                profile={profile}
                title={getTitle(profile.title_id)}
                emblem={getEmblem(profile.emblem_id)}
              />
            </div>
          )}
          {context === 'popup' && <PopupHeaderPreview profile={profile} />}
          {context === 'dm' && (
            <div className="h-[560px] w-[380px] max-w-full overflow-hidden rounded-2xl border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
              <FriendshipInfoRail profile={profile} conversationId={undefined} />
            </div>
          )}
        </ProfileThemePreview>
      </div>
    </div>
  );
}

/** Mirrors UserProfileModal's header block exactly (w 430 / banner 178 / avatar 98). */
function PopupHeaderPreview({ profile }: { profile: Profile }) {
  const title = getTitle(profile.title_id);
  const emblem = getEmblem(profile.emblem_id);
  const theme = useProfileTheme(profile.id, emblem.glow);
  const name = profile.display_name || stripHandle(profile.handle) || 'Member';
  const handle = stripHandle(profile.handle);

  return (
    <div className="w-[min(430px,100%)] overflow-hidden rounded-2xl border border-white/10 bg-[rgba(13,14,20,0.68)] shadow-[0_30px_90px_rgba(0,0,0,0.55)] backdrop-blur-2xl backdrop-saturate-[1.35]">
      <div className="relative h-[178px] overflow-hidden">
        <ProfileBannerSurface theme={theme} fallbackImage={title.bg} fallbackClassName="scale-105" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(255,255,255,0.2),transparent_34%),linear-gradient(180deg,rgba(8,9,12,0.08),rgba(8,9,12,0.82))]" />
      </div>
      <div className="relative px-5 pb-6">
        <div className="-mt-12 flex items-end justify-between gap-3">
          <div
            className="grid h-[98px] w-[98px] flex-none place-items-center overflow-hidden rounded-full border-[4px] border-[#101016] bg-white/10 bg-cover bg-center text-2xl font-black text-white shadow-[0_16px_38px_rgba(0,0,0,0.62),0_0_0_1px_rgba(255,255,255,0.16)]"
            style={{
              ...(profile.avatar_url
                ? { backgroundImage: `url(${profile.avatar_url})` }
                : { background: `linear-gradient(150deg, ${theme.accent}, rgba(255,255,255,0.08))` }),
              ...(theme.doc.frame !== 'none' ? avatarFrameStyle(theme.doc.frame, theme.accent) : {}),
            }}
          >
            {!profile.avatar_url && initials(name)}
          </div>
        </div>
        <div className="mt-3 flex min-w-0 items-center gap-1.5">
          <h2
            className="truncate text-[24px] font-black leading-none tracking-[-0.5px] text-hi"
            style={theme.doc.nameStyle === 'gradient' ? gradientTextStyle(theme.accent) : undefined}
          >
            {name}
          </h2>
          {theme.doc.showEmblem && <EmblemGlyph emblem={emblem} size={21} />}
        </div>
        {handle && <div className="mt-1 truncate text-[13px] font-semibold text-lo">@{handle}</div>}
      </div>
    </div>
  );
}
